import { useState } from 'react';

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'What is virtual staging?',
      answer: 'Virtual staging is the process of digitally adding furniture, decor, and design elements to photos of empty or unfurnished rooms. It helps potential buyers visualize the space and see its full potential without the cost and logistics of physical staging.'
    },
    {
      question: 'How long does the virtual staging process take?',
      answer: 'We deliver your professionally staged images within 24 hours of receiving your photos. For rush orders or large projects, we can often accommodate faster turnaround times. Contact us to discuss your specific timeline needs.'
    },
    {
      question: 'What types of properties can you stage?',
      answer: 'We can virtually stage any type of property including residential homes, luxury estates, commercial spaces, apartments, condos, and vacant land visualizations. Our designers are experienced in various architectural styles and property types.'
    },
    {
      question: 'How much does virtual staging cost?',
      answer: 'Our pricing starts at $24 per photo for the Basic package. We offer volume discounts and custom packages for larger projects. Check our pricing section above for detailed information on all available plans.'
    },
    {
      question: 'What photo quality do you need?',
      answer: 'We work best with high-resolution photos taken with good lighting. Photos should be well-lit, in focus, and show the entire room. We recommend using a wide-angle lens and taking photos from doorway height for the best results.'
    },
    {
      question: 'Can I request revisions?',
      answer: 'Yes! All our packages include free revisions. If you\'d like to adjust furniture placement, change design styles, or modify any elements, simply let us know and we\'ll make the changes at no additional cost.'
    },
    {
      question: 'What design styles do you offer?',
      answer: 'We offer a wide range of design styles including modern, contemporary, traditional, transitional, minimalist, luxury, farmhouse, industrial, and more. You can specify your preferred style when submitting your project, or we can recommend styles based on your target market.'
    },
    {
      question: 'Can you remove existing furniture?',
      answer: 'Yes, we can digitally remove existing furniture and replace it with new staged furniture. This is perfect for properties that are occupied but need a fresh look, or for updating outdated furnishings to appeal to modern buyers.'
    },
    {
      question: 'Are the staged images MLS compliant?',
      answer: 'Yes, our virtually staged images meet MLS requirements. We recommend including a disclosure that images are virtually staged, which we can add to the photos if needed. This ensures transparency while showcasing your property\'s potential.'
    },
    {
      question: 'Do you offer bulk discounts?',
      answer: 'Absolutely! We offer significant discounts for bulk orders and ongoing partnerships. Real estate agencies, property developers, and frequent users can benefit from our volume pricing. Contact us to discuss a custom package tailored to your needs.'
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="py-24 bg-[#F8F6F3]">
      <div className="max-w-4xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-sm text-gray-500 mb-3 flex items-center justify-center gap-2">
            <span className="w-1.5 h-1.5 bg-gray-400 rounded-full"></span>
            FREQUENTLY ASKED QUESTIONS
          </p>
          <h2 className="text-5xl md:text-6xl font-light mb-4">Got Questions?</h2>
          <p className="text-lg text-gray-600">Find answers to common questions about our virtual staging services</p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl overflow-hidden transition-all duration-300 hover:shadow-lg"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 md:px-8 py-6 flex items-center justify-between gap-4 text-left cursor-pointer"
              >
                <h4 className="text-lg md:text-xl font-bold pr-4">{faq.question}</h4>
                <div className={`w-10 h-10 flex items-center justify-center flex-shrink-0 bg-[#F8F6F3] rounded-full transition-transform duration-300 ${openIndex === index ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-2xl text-[#FF6B6B]"></i>
                </div>
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ${
                  openIndex === index ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 md:px-8 pb-6">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-gray-600 mb-4">Still have questions?</p>
          <a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-8 py-3 bg-black text-white text-sm font-medium rounded-full hover:bg-gray-800 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
          >
            Contact Us
            <i className="ri-arrow-right-line"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
